import type { ImageCategory, Strain, StrainInput } from '@shared';
import type { ProductImagesApi } from './productImages';
import type { ProductRepository } from './productRepository';
import { validateLocalImageBlob } from './productImages';

export interface StagedProductImage {
  key: string;
  file: File;
  previewUrl: string;
  imageCategory: ImageCategory;
}

export interface StagedImageUrlApi {
  createObjectURL(blob: Blob): string;
  revokeObjectURL(url: string): void;
}

export interface StagedImageSaveFailure {
  operation: 'upload' | 'primary';
  key: string;
  filename: string;
  message: string;
}

export interface StagedImageSaveResult {
  savedCount: number;
  failures: StagedImageSaveFailure[];
}

export interface CreateProductWithImagesResult {
  product: Strain;
  images: StagedImageSaveResult;
}

function defaultUrlApi(): StagedImageUrlApi {
  return {
    createObjectURL: (blob) => URL.createObjectURL(blob),
    revokeObjectURL: (url) => URL.revokeObjectURL(url),
  };
}

function defaultKey(): string {
  return globalThis.crypto?.randomUUID?.() ??
    `staged-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function appendStagedProductImages(
  current: StagedProductImage[],
  files: Iterable<File>,
  options: {
    urls?: StagedImageUrlApi;
    createKey?: () => string;
  } = {},
): StagedProductImage[] {
  const selected = [...files];
  for (const file of selected) validateLocalImageBlob(file);

  const urls = options.urls ?? defaultUrlApi();
  const createKey = options.createKey ?? defaultKey;
  const appended: StagedProductImage[] = [];
  try {
    for (const file of selected) {
      appended.push({
        key: createKey(),
        file,
        previewUrl: urls.createObjectURL(file),
        imageCategory: 'package_front',
      });
    }
  } catch (error) {
    for (const image of appended) urls.revokeObjectURL(image.previewUrl);
    throw error;
  }
  return [...current, ...appended];
}

export function removeStagedProductImage(
  current: StagedProductImage[],
  key: string,
  urls: StagedImageUrlApi = defaultUrlApi(),
): StagedProductImage[] {
  const removed = current.find((image) => image.key === key);
  if (removed) urls.revokeObjectURL(removed.previewUrl);
  return current.filter((image) => image.key !== key);
}

export function revokeStagedProductImages(
  images: Iterable<StagedProductImage>,
  urls: StagedImageUrlApi = defaultUrlApi(),
): void {
  for (const image of images) urls.revokeObjectURL(image.previewUrl);
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Image could not be saved.';
}

/**
 * Saves images sequentially because both existing transports persist one image
 * per request/transaction. Returned hydrated products identify each new image,
 * allowing Primary to be changed without changing selection order.
 */
export async function persistStagedProductImages(
  productId: string,
  images: readonly StagedProductImage[],
  primaryKey: string | null,
  api: ProductImagesApi,
): Promise<StagedImageSaveResult> {
  const failures: StagedImageSaveFailure[] = [];
  const savedIds = new Map<string, string>();
  const knownIds = new Set<string>();

  for (const image of images) {
    try {
      const updated = await api.upload(
        productId,
        image.file,
        image.imageCategory,
      );
      const added = updated.images.find((candidate) => !knownIds.has(candidate.id));
      for (const candidate of updated.images) knownIds.add(candidate.id);
      if (!added) {
        throw new Error('Saved image could not be identified.');
      }
      savedIds.set(image.key, added.id);
    } catch (error) {
      failures.push({
        operation: 'upload',
        key: image.key,
        filename: image.file.name || 'Selected image',
        message: errorMessage(error),
      });
    }
  }

  const primaryId = primaryKey ? savedIds.get(primaryKey) : undefined;
  if (primaryId && primaryKey !== images[0]?.key) {
    try {
      await api.setPrimary(primaryId);
    } catch (error) {
      failures.push({
        operation: 'primary',
        key: primaryKey ?? 'primary',
        filename:
          images.find((image) => image.key === primaryKey)?.file.name ||
          'Primary image',
        message: `Image saved, but Primary could not be set: ${errorMessage(error)}`,
      });
    }
  }

  return { savedCount: savedIds.size, failures };
}

export async function createProductWithStagedImages(
  input: StrainInput,
  images: readonly StagedProductImage[],
  primaryKey: string | null,
  products: ProductRepository,
  imageApi: ProductImagesApi,
): Promise<CreateProductWithImagesResult> {
  const product = await products.create(input);
  const imageResult = await persistStagedProductImages(
    product.id,
    images,
    primaryKey,
    imageApi,
  );
  return { product, images: imageResult };
}
